import React from 'react'
import { TFunction } from 'i18next'

import { FormattedAmountValues, FormattedTransactionData } from './types'
import { Avatar } from '../Avatar'
import { Copy } from '../Copy'
import { ExpandableNote } from '../ExpandableNote'

import { Transaction } from '@/domain/transactions'
import { formatAddress } from '@/utils/formatAddress'
import {
  transactionUtils,
  TransactionTypes,
  generateRandomId,
  capitalizeFirstLetter,
} from '@/utils/transactionUtils'

export const formatTransactionData = (
  transaction: Transaction,
  t: TFunction<'translation', undefined>,
): FormattedTransactionData[] => {
  const renderWhenIs = (list: TransactionTypes[]) =>
    list.includes(transaction.type as TransactionTypes)

  const {
    isReceive,
    isPurchase,
    isDeposit,
    isWithdrew,
    isKemToKem,
    isReferralReward,
    isSignUpReward,
    isRefunded,
    isFailed,
    isCompleted,
    isPending,
    isCardPurchase,
    isSavingsTransferIn,
  } = transactionUtils.getTransactionConditionalsToRenderLayouts({
    direction: transaction.direction,
    type: transaction.type as TransactionTypes,
    state: transaction.state,
    rewardType: transaction.subject_reward?.reward_type,
  })
  // console.log(isReferralReward)
  // console.log(isSignUpReward)

  const isKemToKemReceiver = isReceive && isKemToKem

  const formatAmountValues = (): FormattedAmountValues => {
    const valueDefault =
      transaction?.formattedAmount ||
      `${transaction.amount?.amount || '0'} ${transaction.amount?.currency || ''}`

    const shouldShowRelativeFiatAmount =
      isDeposit ||
      isKemToKem ||
      isWithdrew ||
      isPurchase ||
      isReferralReward ||
      isSignUpReward

    let formattedAmountValue: FormattedAmountValues = {
      value:
        transaction?.formattedFiatAmount ||
        `$${transaction.amount?.price_for_unit || '0'}`,
    }

    if (shouldShowRelativeFiatAmount) {
      formattedAmountValue = {
        value: valueDefault,
        secondaryValue: `≈${transaction?.formattedFiatAmount || `$${transaction.amount?.price_for_unit || '0'}`}`,
      }
    } else if (isWithdrew) {
      formattedAmountValue = {
        value: valueDefault,
      }
    } else if (isCardPurchase) {
      formattedAmountValue = {
        value:
          transaction?.formattedFiatAmountWithoutFee ||
          transaction?.formattedFiatAmount ||
          valueDefault,
      }
    }

    return formattedAmountValue
  }

  const formatTotalValues = (): FormattedAmountValues => {
    const shouldShowRelativeCryptoAmount = isCardPurchase

    let formattedAmountValue: FormattedAmountValues = {
      value:
        transaction.formattedAmount ||
        `${transaction.amount?.amount || '0'} ${transaction.amount?.currency || ''}`,
      secondaryValue: `≈${transaction.formattedFiatAmount || `$${transaction.amount?.price_for_unit || '0'}`}`,
    }

    if (shouldShowRelativeCryptoAmount) {
      formattedAmountValue = {
        value:
          transaction?.formattedFiatAmount ||
          `$${transaction.amount?.price_for_unit || '0'}`,
        secondaryValue: `≈${transaction?.formattedAmount || `${transaction.amount?.amount || '0'} ${transaction.amount?.currency || ''}`}`,
      }
    }

    return formattedAmountValue
  }

  const getState = (): string => {
    if (isRefunded) return 'transactionStates.refunded'
    if (isCompleted) return 'translation:completed'
    if (isPending) return 'translation:pending'
    if (isFailed) return 'translation:failed'

    return 'common:unavailable'
  }

  const paymentMethodIsVisible = () =>
    transaction.subject_top_up?.payment_method || isSavingsTransferIn

  const getPaymentMethod = () => {
    if (transaction.subject_top_up?.payment_method === 'ApplePay') {
      return 'Apple Pay'
    } else if (transaction.subject_top_up?.payment_method) {
      return transaction.subject_top_up?.payment_method
    } else if (isSavingsTransferIn) {
      return 'USDT Wallet'
    }

    return ''
  }

  return [
    {
      id: generateRandomId(),
      key: 'amount',
      label: t('translation:amount'),
      isVisible: renderWhenIs([
        'on_ramp_top_up',
        'external_deposit',
        'external_withdraw',
        'kem_to_kem',
        'on_ramp_send',
        'kem_reward',
        'card_write_off',
        'saving_transfer_in',
        'saving_transfer_out',
      ]),
      ...formatAmountValues(),
    },
    {
      id: generateRandomId(),
      key: 'amount-sold',
      label: isFailed
        ? t('translation:amount')
        : t('translation:amountYouSold'),
      value:
        transaction.formattedAmount ||
        `${transaction.amount?.amount || '0'} ${transaction.amount?.currency || ''}`,
      isVisible: renderWhenIs(['swap_sell']),
    },
    {
      id: generateRandomId(),
      key: 'from',
      label: t('translation:from'),
      value: isSignUpReward
        ? t('translation:signUpBonusFromKem')
        : t('translation:paymentFromReferral'),
      // secondLineValue: isSignUpReward ? t('translation:forJoiningTheApp') : undefined,
      isVisible: renderWhenIs(['kem_reward']),
    },
    {
      id: generateRandomId(),
      key: 'amount-converted',
      label: isFailed
        ? t('translation:amount')
        : t('translation:amountYouConverted'),
      isVisible: renderWhenIs(['swap_convert']),
      ...formatTotalValues(),
    },
    {
      id: generateRandomId(),
      key: 'transaction-fee',
      label: t('transactions:rowItemLabels.transactionFee'),
      value:
        transaction.formattedFee ||
        (transaction.fee
          ? `${transaction.fee.amount} ${transaction.fee.currency}`
          : ''),
      isVisible:
        !isFailed &&
        !isKemToKemReceiver &&
        transaction.hasFee &&
        renderWhenIs([
          'external_withdraw',
          'swap_sell',
          'kem_to_kem',
          'on_ramp_send',
          'swap_convert',
          'card_write_off',
          'saving_transfer_in',
          'saving_transfer_out',
        ]),
    },
    {
      id: generateRandomId(),
      key: 'card-fee',
      label: t('translation:cardAuthorizationFee'),
      value: transaction.formattedCardFee || '',
      isVisible: renderWhenIs(['card_write_off']),
    },
    {
      id: generateRandomId(),
      key: 'total',
      label: t('transactions:rowItemLabels.total'),
      ...formatTotalValues(),
      isVisible: !isFailed && renderWhenIs(['card_write_off']),
    },
    {
      id: generateRandomId(),
      key: 'details',
      label: t('translation:details'),
      value: transaction.subject_card?.details || '',
      isVisible: renderWhenIs(['card_write_off']),
    },
    {
      id: generateRandomId(),
      key: 'account-deduction',
      label: t('translation:accountDeduction'),
      value: `${transaction.amount?.currency || ''} ${t('translation:balance')}`,
      isVisible: renderWhenIs(['card_write_off']),
    },
    {
      id: generateRandomId(),
      key: 'rate',
      label: t('translation:rate'),
      value: transaction.formattedRate || '',
      isVisible: !isFailed && renderWhenIs(['swap_convert']),
    },
    {
      id: generateRandomId(),
      key: 'total-paid',
      label: t('translation:totalPaid'),
      value: transaction.formattedPaymentAmount || '',
      isVisible:
        !!transaction.formattedPaymentAmount &&
        !isKemToKemReceiver &&
        renderWhenIs(['on_ramp_top_up', 'on_ramp_send']),
    },
    {
      id: generateRandomId(),
      key: 'payment-method',
      label: t('translation:paidWith'),
      value: getPaymentMethod(),
      isVisible:
        paymentMethodIsVisible() &&
        renderWhenIs([
          'on_ramp_top_up',
          'kem_to_kem',
          'on_ramp_send',
          'saving_transfer_in',
        ]),
    },
    {
      id: generateRandomId(),
      key: 'note-from',
      label: t('translation:note'),
      value: String(transaction.purpose?.slice(0, 26) || '').trim(),
      isVisible:
        !!transaction.purpose && renderWhenIs(['kem_to_kem', 'on_ramp_send']),
      Icon: () => (
        <ExpandableNote
          subjectFullName={`${transaction.subject_user?.first_name || ''} ${transaction.subject_user?.last_name || ''}`}
          note={transaction.purpose || ''}
        />
      ),
    },
    {
      id: generateRandomId(),
      key: 'amount-received',
      label: t('translation:amountYouReceived'),
      value: transaction.formattedCreditAmount || '',
      isVisible: !isFailed && renderWhenIs(['swap_sell', 'swap_convert']),
    },
    {
      id: generateRandomId(),
      key: 'crypto-network',
      label: t('translation:cryptoNetwork'),
      value: transaction?.formattedNetwork?.name || t('translation:unnamed'),
      isVisible: renderWhenIs(['external_deposit', 'external_withdraw']),
      Icon: () => (
        <Avatar
          isCurrency
          size={22}
          uri={transaction?.formattedNetwork?.imageUrl || ''}
          iconSize={22}
        />
      ),
      iconPosition: 'left',
    },
    {
      id: generateRandomId(),
      key: 'deposit-address',
      label: t('translation:depositAddress'),
      value: formatAddress(transaction.formattedDepositAddress || ''),
      isVisible: renderWhenIs(['external_deposit', 'external_withdraw']),
      Icon: () => (
        <Copy
          toastMessage={`${t('translation:depositAddress')} ${t('translation:copied')}`}
          copyValue={transaction.formattedDepositAddress || ''}
        />
      ),
      iconPosition: 'right',
    },
    {
      id: generateRandomId(),
      key: 'date',
      label: t(isRefunded ? 'refundDate' : 'date'),
      value: transactionUtils.formatDateByDeviceRegion({
        date: isRefunded ? transaction.updated_at : transaction.created_at,
        isTitle: true,
        showTime: true,
      }),
      isVisible: true,
    },
    {
      id: generateRandomId(),
      key: 'transaction-id',
      label: t('translation:transactionId'),
      value: formatAddress(transaction.transaction_id),
      isVisible: true,
      Icon: () => (
        <Copy
          toastMessage={`${t('translation:transactionId')} ${t('translation:copied')}`}
          copyValue={transaction.transaction_id}
        />
      ),
      iconPosition: 'right',
    },
    {
      id: generateRandomId(),
      key: 'status',
      unformattedValue: transaction.state,
      label: t('translation:status'),
      value: capitalizeFirstLetter(t(getState())),
      isVisible: true,
    },
  ]
}
